import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { 
  Calendar, Users, MapPin, Eye, Download, Mail, Clock, 
  CheckCircle, XCircle, AlertCircle, FileText, Camera, Map, AlertTriangle, FileSpreadsheet, Flag
} from 'lucide-react';
import { toast } from 'sonner';
import PilotAlertsPanel from '@/components/PilotAlertsPanel';
import PhotoReviewQueue from '@/components/PhotoReviewQueue';
import PilotProgressTable from '@/components/PilotProgressTable';

function PilotProgressButton({ event }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="h-10 border-gray-300 text-gray-800" data-testid={`pilot-progress-btn-${event.id}`}>
        <Users className="w-4 h-4 mr-2" />
        Progreso pilotos
      </Button>
      <PilotProgressTable eventId={event.id} eventTitle={event.title} open={open} onOpenChange={setOpen} />
    </>
  );
}

function ReviewQueueButton({ event }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    axios.get(`${API}/rally-master/events/${event.id}/review-queue`, { params: { status: 'pending' } })
      .then((r) => setPending(r.data.counts.pending))
      .catch(() => {});
  }, [event.id]);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className={`relative h-10 ${pending ? 'border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100' : 'border-gray-300 text-gray-800'}`}
        data-testid={`review-queue-btn-${event.id}`}
      >
        <Camera className="w-4 h-4 mr-2" />
        Fotos pendientes
        {pending != null && (
          <span className={`ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 rounded-full text-xs font-bold ${pending ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-700'}`} data-testid={`review-queue-count-${event.id}`}>
            {pending}
          </span>
        )}
      </Button>
      <PhotoReviewQueue eventId={event.id} eventTitle={event.title} open={open} onOpenChange={setOpen} onCountChange={setPending} />
    </>
  );
}

export default function RallyMasterDashboard({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rally-master/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const openEventRegistrations = async (event) => {
    setSelectedEvent(event);
    setShowRegistrationsModal(true);
    setLoadingRegistrations(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/rally-master/events/${event.id}/registrations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistrations(response.data.registrations || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Error al cargar inscripciones');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const updatePaymentStatus = async (registrationId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/rally-master/registrations/${registrationId}/payment-status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Estado de pago actualizado a ${newStatus}`);
      
      // Update local state
      setRegistrations(prev => prev.map(reg => 
        reg.id === registrationId ? { ...reg, payment_status: newStatus } : reg
      ));
    } catch (error) {
      toast.error('Error al actualizar estado de pago');
    }
  };

  const cancelRegistration = async (registrationId, userName) => {
    if (!confirm(`¿Estás seguro de cancelar la inscripción de ${userName}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/rally-master/registrations/${registrationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Inscripción cancelada');
      setRegistrations(prev => prev.filter(reg => reg.id !== registrationId));
    } catch (error) {
      toast.error('Error al cancelar inscripción');
    }
  };

  const sendReminders = async (eventId) => {
    if (!confirm('¿Enviar recordatorio a todos los participantes?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/rally-master/events/${eventId}/send-reminder`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Error al enviar recordatorios');
    }
  };

  const exportCSV = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/rally-master/events/${eventId}/export-csv`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inscripciones_${eventId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV descargado');
    } catch (error) {
      toast.error('Error al exportar CSV');
    }
  };

  const exportTrackingReport = async (eventId, eventTitle) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/rally-master/events/${eventId}/export-tracking-report`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const safeTitle = eventTitle.replace(/[^a-zA-Z0-9 _-]/g, '').replace(/ /g, '_').substring(0, 30);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tracking_report_${safeTitle}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Reporte de tracking descargado');
    } catch (error) {
      console.error('Error exporting tracking report:', error);
      toast.error('Error al exportar reporte de tracking');
    }
  };

  const openLiveMap = (eventId) => {
    navigate(`/rally-master/live-map/${eventId}`);
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesPayment = paymentFilter === 'all' || reg.payment_status === paymentFilter;
    const matchesSearch = !searchQuery || 
      reg.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPayment && matchesSearch;
  });

  const getPaymentStats = () => {
    const paid = registrations.filter(r => r.payment_status === 'paid').length;
    const pending = registrations.filter(r => r.payment_status === 'pending').length;
    const cancelled = registrations.filter(r => r.payment_status === 'cancelled').length;
    return { paid, pending, cancelled, total: registrations.length };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="relative bg-[#111111] text-white overflow-hidden" data-testid="rally-master-header">
        <div className="absolute inset-x-0 top-0 h-3 checkered-flag" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-4 pt-9 pb-8 flex items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-orange-400 font-semibold mb-1">Control de carrera</p>
            <h1 className="text-3xl font-bold">Panel Rally Master</h1>
            <p className="text-gray-400 mt-1">Gestiona los eventos asignados</p>
          </div>
          <Flag className="w-10 h-10 text-orange-500 shrink-0 hidden sm:block" />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-3 checkered-flag" aria-hidden="true" />
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No tienes eventos asignados como Rally Master</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Próximos Eventos - ordenados por fecha más cercana primero */}
            {events.filter(e => e.status !== 'completed').length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                  Próximos Eventos
                </h2>
                <div className="grid gap-6">
                  {events
                    .filter(e => e.status !== 'completed')
                    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
                    .map(event => (
                      <div key={event.id} className="space-y-4">
                        <Card className="overflow-hidden">
                          <div className="flex flex-col md:flex-row">
                            {/* Event Image */}
                            {event.poster_url && (
                              <div className="md:w-48 h-32 md:h-auto">
                                <img 
                                  src={event.poster_url} 
                                  alt={event.title}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            
                            {/* Event Info */}
                            <div className="flex-1 p-6">
                              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                <div>
                                  <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      {event.start_date}
                                    </span>
                                    {event.location && (
                                      <span className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        {event.location}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex gap-2 mt-2">
                                    <Badge variant={event.status === 'active' ? 'default' : 'secondary'}>
                                      {event.status === 'active' ? 'Activo' : event.status}
                                    </Badge>
                                    {(event.alert_deviation_enabled || event.alert_speed_enabled) && (
                                      <Badge variant="outline" className="border-red-300 text-red-600">
                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                        Alertas activas
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Actions */}
                                <div className="flex flex-wrap gap-2">
                                  <Button 
                                    onClick={() => openEventRegistrations(event)}
                                    className="bg-gray-900 hover:bg-black text-white"
                                  >
                                    <Users className="w-4 h-4 mr-2" />
                                    Ver Inscritos
                                  </Button>
                                  <Button 
                                    onClick={() => openLiveMap(event.id)}
                                    variant="outline"
                                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                  >
                                    <Map className="w-4 h-4 mr-2" />
                                    Mapa en Vivo
                                  </Button>
                                  {event.requires_tracking && <ReviewQueueButton event={event} />}
                                  {event.event_mode === 'free' && <PilotProgressButton event={event} />}
                                  <Button
                                    onClick={() => exportCSV(event.id)}
                                    variant="outline"
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    CSV
                                  </Button>
                                  {(event.alert_deviation_enabled || event.alert_speed_enabled) && (
                                    <Button
                                      onClick={() => exportTrackingReport(event.id, event.title)}
                                      variant="outline"
                                      className="border-green-300 text-green-700"
                                    >
                                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                                      Excel Tracking
                                    </Button>
                                  )}
                                  <Button
                                    onClick={() => sendReminders(event.id)}
                                    variant="outline"
                                  >
                                    <Mail className="w-4 h-4 mr-2" />
                                    Recordatorio
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                        
                        {/* Pilot Alerts Panel - only show for events with alerts enabled */}
                        {(event.alert_deviation_enabled || event.alert_speed_enabled) && (
                          <PilotAlertsPanel eventId={event.id} eventTitle={event.title} />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Eventos Realizados - ordenados por fecha más reciente primero */}
            {events.filter(e => e.status === 'completed').length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-3 h-3 bg-gray-400 rounded-full"></span>
                  Eventos Realizados
                </h2>
                <div className="grid gap-6">
                  {events
                    .filter(e => e.status === 'completed')
                    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
                    .map(event => (
                      <Card key={event.id} className="overflow-hidden opacity-80">
                        <div className="flex flex-col md:flex-row">
                          {/* Event Image */}
                          {event.poster_url && (
                            <div className="md:w-48 h-32 md:h-auto">
                              <img 
                                src={event.poster_url} 
                                alt={event.title}
                                className="w-full h-full object-cover grayscale-[30%]"
                              />
                            </div>
                          )}
                          
                          {/* Event Info */}
                          <div className="flex-1 p-6">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                              <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {event.start_date}
                                  </span>
                                  {event.location && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {event.location}
                                    </span>
                                  )}
                                </div>
                                <Badge className="mt-2 bg-gray-500">
                                  Completado
                                </Badge>
                              </div>
                              
                              {/* Actions */}
                              <div className="flex flex-wrap gap-2">
                                <Button 
                                  onClick={() => openEventRegistrations(event)}
                                  className="bg-gray-900 hover:bg-black text-white"
                                >
                                  <Users className="w-4 h-4 mr-2" />
                                  Ver Inscritos
                                </Button>
                                {event.requires_tracking && <ReviewQueueButton event={event} />}
                                  {event.event_mode === 'free' && <PilotProgressButton event={event} />}
                                <Button
                                  onClick={() => exportCSV(event.id)}
                                  variant="outline"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  CSV
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Registrations Modal */}
      <Dialog open={showRegistrationsModal} onOpenChange={setShowRegistrationsModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-600" />
              Inscripciones - {selectedEvent?.title}
            </DialogTitle>
            <DialogDescription>
              Gestiona las inscripciones del evento
            </DialogDescription>
          </DialogHeader>

          {loadingRegistrations ? (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{getPaymentStats().total}</p>
                  <p className="text-xs text-gray-600">Total</p>
                </div>
                <div className="bg-green-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{getPaymentStats().paid}</p>
                  <p className="text-xs text-gray-600">Pagados</p>
                </div>
                <div className="bg-yellow-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-600">{getPaymentStats().pending}</p>
                  <p className="text-xs text-gray-600">Pendientes</p>
                </div>
                <div className="bg-red-100 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{getPaymentStats().cancelled}</p>
                  <p className="text-xs text-gray-600">Cancelados</p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <Input
                  placeholder="Buscar por nombre o email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="all">Todos</option>
                  <option value="paid">Pagados</option>
                  <option value="pending">Pendientes</option>
                  <option value="cancelled">Cancelados</option>
                </select>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left">Nombre</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Teléfono</th>
                      <th className="px-3 py-2 text-left">Moto</th>
                      <th className="px-3 py-2 text-left">Pago</th>
                      <th className="px-3 py-2 text-left">Precio</th>
                      <th className="px-3 py-2 text-left">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRegistrations.map(reg => (
                      <tr key={reg.id} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{reg.full_name}</td>
                        <td className="px-3 py-2">{reg.email}</td>
                        <td className="px-3 py-2">{reg.phone}</td>
                        <td className="px-3 py-2">
                          {reg.moto_brand} {reg.moto_model}
                          {reg.moto_plate && <span className="text-gray-500 ml-1">({reg.moto_plate})</span>}
                        </td>
                        <td className="px-3 py-2">
                          <select
                            value={reg.payment_status}
                            onChange={(e) => updatePaymentStatus(reg.id, e.target.value)}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              reg.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                              reg.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}
                          >
                            <option value="pending">Pendiente</option>
                            <option value="paid">Pagado</option>
                            <option value="cancelled">Cancelado</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">{reg.final_price}€</td>
                        <td className="px-3 py-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancelRegistration(reg.id, reg.full_name)}
                            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredRegistrations.length === 0 && (
                <div className="py-8 text-center text-gray-500">
                  No hay inscripciones que coincidan con los filtros
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
